import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useStores } from '@/hooks/use-stores';
import { ComponentTierName, interestedPackageEnum } from '@/constants/enums';
import { checkTierPermission } from '@/utils/util-functions';
import EPTierLockCover from '@/comp/common/EPTierLockCover';

import Card from '../Card';
import PowerTab from './PowerTab';
import SpecificPowerTab from './SpecificPowerTab';
import YearPager from '../YearPager';
import YearlyViewContainer from '../YearlyViewContainer';

const EnergyEfficiencyYearlyView = observer(
  ({
    tabKey,
    animation,
    className,
    style,
    ...rest
  }: {
    tabKey: string;
    animation?: boolean;
    className?: string;
    style?: Record<string, unknown>;
  }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { energySavingsGraphStore, energySavingsStore, stationStore } = useStores();
    const shouldRestrictEnergySavings = checkTierPermission(ComponentTierName.EnergySavingsWidget, stationStore);

    const onChangeTab = value => {
      navigate(`../${value}`);
    };

    useEffect(() => {
      if (!shouldRestrictEnergySavings) energySavingsGraphStore.resetGraph();
    }, [energySavingsGraphStore, shouldRestrictEnergySavings]);

    useEffect(() => {
      if (!shouldRestrictEnergySavings) {
        energySavingsGraphStore.resetGraphOnYearChange();
        energySavingsGraphStore.getGraphByYear(energySavingsStore.requestedYear);
      }
    }, [energySavingsGraphStore, energySavingsStore.requestedYear, shouldRestrictEnergySavings]);

    const tabItems = [
      {
        label: t('Power'),
        key: 'power',
        children: <PowerTab yearToPresent={energySavingsStore.requestedYear} animation={animation} />,
      },
      {
        label: t('Specific Power'),
        key: 'specific-power',
        children: <SpecificPowerTab yearToPresent={energySavingsStore.requestedYear} />,
      },
    ];

    return (
      <YearlyViewContainer className={className} style={style} {...rest}>
        <div className="card">
          {shouldRestrictEnergySavings && (
            <EPTierLockCover
              label={t('learnMoreAndEnableSystemControl')}
              tooltipTextPart2={t('alwaysPerform')}
              tooltipPlacement="bottom"
              tooltipWidth={300}
              message={t('backgroundDataYearlyView')}
              emailRequestTypeOfInterestedPackage={interestedPackageEnum.SYSTEM_CONTROL}
              emailRequestButtonClicked={'Lock Button'}
              emailRequestWhichScreen={'Value - Yearly View'}
            />
          )}
          <Card className="efficiency-card">
            <Tabs
              activeKey={tabKey}
              animated={{
                inkBar: true,
                tabPane: false,
              }}
              onChange={onChangeTab}
              items={tabItems}
            />
            <div className={'footer'}>
              <span className={'asterisk'}>* </span>
              {t('SHOWING_CURRENT_MONTH_ACCUMULATED_DATA')}
              <YearPager energySavingsStore={energySavingsStore} yearOffsetLimit={energySavingsGraphStore.lastPage} />
            </div>
          </Card>
        </div>
      </YearlyViewContainer>
    );
  },
);

export default EnergyEfficiencyYearlyView;
